import React from 'react';

import { Typography, Box, Grid2 as Grid, Container, Icon} from '@mui/material';

import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import Occupancy, { OccupancyState } from './icons/Occupancy';
import Carriage from './Carriage';


function getOccupancyState(occupancy: number): OccupancyState {
    if (occupancy < 30) {
        return OccupancyState.low;
    } else if (occupancy < 70) {
        return OccupancyState.medium;
    } else {
        return OccupancyState.high;
    }
}

type TrainProps = {
    station: string;
    time: string;
    delay: number;
    occupancy: number;
    passengers: number;
    cars: OccupancyState[][];
    occupancyLevel?: OccupancyState;
    silhouette?: boolean;
    terminus?: boolean;
    // Three distinct occupancy states, which the UI previously collapsed into two:
    //   measured  the train called here and sensors reported  -> obsArrival set
    //   forecast  VU prognose horizon 1-5 for a stop ahead    -> forecast=true
    //   none      beyond the forecast horizon                 -> silhouette
    // Without this, a prediction for a stop the train has not reached renders
    // identically to a real count.
    forecast?: boolean;
    // live2: the occupancy state as an EXPLICIT value, replacing the inference
    // above. When supplied it wins: `forecast`/`silhouette` were derived from which
    // fields happened to be populated, which is exactly the guesswork the new
    // Firestore schema exists to remove. Old call sites omit it and behave as
    // before, so /live is unaffected.
    occupancyState?: 'MEASURED' | 'AWAITING' | 'MISSING' | 'FORECAST' | 'NONE';
    missingReason?: string | null;
    // live2 translation hooks. Optional so /live keeps its German labels with no
    // change at all: when absent the hard-coded defaults below apply.
    stateLabel?: string | null;   // overrides the badge text
    arrLabel?: string;            // "An." / "Arr."
    depLabel?: string;            // "Ab." / "Dep."
    arrivedLabel?: string;        // terminus, arrival observed
    expectedArrivalLabel?: string; // terminus, arrival still expected
    // Planned vs observed, per stop. Observed present => the event happened.
    schedArrival?: string | null;
    schedDeparture?: string | null;
    obsArrival?: string | null;
    obsDeparture?: string | null;
    delayArrivalSec?: number | null;
    delayDepartureSec?: number | null;
    sx?: React.CSSProperties;
};

// Delay to nearest minute. Sign is rail convention: + late, - early. Null means
// not yet known (no observation), which is deliberately different from 0.
const delayLabel = (sec?: number | null): string | null => {
    if (sec === null || sec === undefined) return null;
    const mins = Math.round(sec / 60);
    if (mins === 0) return null;
    return `(${mins > 0 ? '+' : ''}${mins})`;
};

// One "Ab. 17:01 (+2)" / "An. 16:58" element. Prefers the observed time when the
// event has happened, falling back to the plan.
const TimeCell = ({ label, sched, obs, delaySec }: {
    label: string;
    sched?: string | null;
    obs?: string | null;
    delaySec?: number | null;
}) => {
    const shown = obs || sched;
    if (!shown) return null;
    const d = delayLabel(delaySec);
    return (
        <Box component="span" sx={{ whiteSpace: 'nowrap', ml: '8px' }}>
            <Box component="span" sx={{ color: '#757575', fontSize: '13px' }}>{label} </Box>
            {shown}
            {d && <Box component="span" sx={{ color: '#d32f2f', fontWeight: 600 }}> {d}</Box>}
        </Box>
    );
};


const Train = (props: TrainProps) => {

    const state = props.occupancyLevel ?? getOccupancyState(props.occupancy);

    // The terminus has no departure and carries no occupancy, so it gets its own
    // treatment: tinted row, and arrival phrased as expected vs actual.
    const hasArrived = Boolean(props.obsArrival);
    const terminusArrival = props.obsArrival || props.schedArrival;
    const terminusDelay = delayLabel(props.delayArrivalSec);

    // Measured beats forecast: once the stop has been called, the counts are real.
    // Legacy path only — when occupancyState is given, the writer already decided.
    const isForecast = props.occupancyState
        ? props.occupancyState === 'FORECAST'
        : (!props.terminus && !hasArrived && props.forecast === true);

    // No number to show unless a real measurement or forecast exists behind it.
    const noFigure = props.occupancyState
        ? (props.occupancyState === 'NONE'
            || props.occupancyState === 'AWAITING'
            || props.occupancyState === 'MISSING')
        : Boolean(props.silhouette);

    const badge = (() => {
        const withLabel = (b: { text: string; fg: string; bg: string; bd: string }) =>
            ({ ...b, text: props.stateLabel ?? b.text });
        switch (props.occupancyState) {
            case 'FORECAST':
                return withLabel({ text: 'Prognose', fg: '#7a5b00', bg: '#fdf0cc', bd: '#e8c76a' });
            case 'AWAITING':
                return withLabel({ text: 'Zählung läuft', fg: '#546e7a', bg: '#eceff1', bd: '#cfd8dc' });
            case 'MISSING':
                return withLabel({ text: 'keine Zählung', fg: '#b3261e', bg: '#fdecec', bd: '#f0b4b0' });
            default:
                return isForecast
                    ? { text: 'Prognose', fg: '#7a5b00', bg: '#fdf0cc', bd: '#e8c76a' }
                    : null;
        }
    })();

    return (
        <>
          <Grid container spacing={0} sx={{
              paddingTop: '34px',
              minWidth: '622px',
              ...(props.terminus ? {
                  backgroundColor: '#eef2f7',
                  borderLeft: '3px solid #4a7ba7',
                  borderRadius: '3px',
                  paddingTop: '10px',
                  paddingBottom: '10px',
                  marginTop: '24px',
              } : {}),
              ...(props.sx || {}),
          }}>
                <Grid size={5}>
                    <Typography fontSize={15.5} color='#000000' align="left">
                        <Icon style={{ color: '#757575', position: "relative", top: "6px" }} component={LocationOnIcon} />
                        {props.station}
                    </Typography>
                </Grid>
                <Grid size={3}>
                    <Typography fontSize={15.5} color='#000000' align="left">
                        {!props.terminus && (<>
                            <Occupancy style={{ position: 'relative', top: '6px', width: '33px' }} state={ state } mainColor={ props.silhouette ? '#B8BEC6' : undefined } />
                            {noFigure ? '—' : props.passengers}
                            {badge && (
                                <Box component="span" title={props.missingReason ?? undefined} sx={{
                                    ml: '6px', px: '4px', py: '1px',
                                    fontSize: '10px', fontWeight: 600,
                                    color: badge.fg, backgroundColor: badge.bg,
                                    border: `1px solid ${badge.bd}`, borderRadius: '3px',
                                    verticalAlign: 'middle',
                                }}>
                                    {badge.text}
                                </Box>
                            )}
                        </>)}
                    </Typography>
                </Grid>
                <Grid size={4} >
                    <Typography fontSize={15.5} color='#000000' align="right">
                        {props.terminus ? (
                            terminusArrival ? (
                                <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                                    <Box component="span" sx={{ color: '#3c5a72', fontSize: '13px' }}>
                                        {hasArrived
                                            ? (props.arrivedLabel ?? 'Angekommen') + ' '
                                            : (props.expectedArrivalLabel ?? 'Erwartete Ankunft') + ' '}
                                    </Box>
                                    {terminusArrival}
                                    {terminusDelay && (
                                        <Box component="span" sx={{ color: '#d32f2f', fontWeight: 600 }}> {terminusDelay}</Box>
                                    )}
                                </Box>
                            ) : props.time
                        ) : (
                            <>
                                <TimeCell label={props.arrLabel ?? 'An.'} sched={props.schedArrival} obs={props.obsArrival} delaySec={props.delayArrivalSec} />
                                <TimeCell label={props.depLabel ?? 'Ab.'} sched={props.schedDeparture} obs={props.obsDeparture} delaySec={props.delayDepartureSec} />
                                {!props.schedArrival && !props.schedDeparture && props.time}
                            </>
                        )}
                        <Icon style={{ color: '#757575', position: "relative", top: "6px" }} component={AccessTimeIcon} />
                        
                    </Typography>
                </Grid>
          </Grid>
          <Grid container spacing={1} sx={{ minWidth: '622px' }}>
                { !props.terminus && props.cars.map((cars, index) => (
                    <Grid size={4} key={index}>
                        <Carriage cars={cars} silhouette={props.silhouette} />
                    </Grid>
                ))}
          </Grid>
        </>
    );
};

export default Train;